import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    TableCell as MuiTableCell,
    TableHead as MuiTableHead,
    TableRow as MuiTableRow,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core';

import { Typography } from '@klp/design';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { useKlpStyles } from '@klp/design/Theme';

import type { ITableConsumerState } from './ITableConsumerState';
import type { TableColumnDef } from './TableColumnDef';
import { TableContext } from './TableContext';

const styles = (theme: Theme) => ({
    cellTitle: {
        textAlign: 'left',
        textTransform: 'uppercase',
        fontWeight: 500,
    },
    headerCell: {
        borderBottom: '1px solid transparent',
        borderBottomColor: theme.palette.primary.main,
        padding: theme.spacing(1),
        whiteSpace: 'nowrap',
    },
    headerCellWrapper: {
        display: 'flex',
        alignItems: 'center',
        '& > *': {
            display: 'flex',
        },
    },
    asc: {
        transform: 'rotate(-90deg)',
        margin: theme.spacing(1),
        cursor: 'pointer',
    },
    desc: {
        transform: 'rotate(90deg)',
        margin: theme.spacing(1),
        cursor: 'pointer',
    },
});

export const TableHead = <DataType, FilterType>(): React.ReactElement => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const context = useContext(TableContext) as ITableConsumerState<
        DataType,
        FilterType
    >;

    return (
        <MuiTableHead>
            <MuiTableRow>
                {context.columns?.map(
                    (column: TableColumnDef<DataType, FilterType>, _index) => (
                        <MuiTableCell
                            className={classes.headerCell}
                            key={column.key}
                            data-testid={column.key}
                        >
                            <div className={classes.headerCellWrapper}>
                                <Typography
                                    className={classes.cellTitle}
                                    klpTypographyVariant="xsmall"
                                >
                                    {t(column.translation)}
                                </Typography>
                                {column.sortable &&
                                    context.sortBy &&
                                    column.key === context.sortBy.key && (
                                        <Illustration
                                            className={
                                                classes[
                                                    context.sortByDirection ||
                                                        'asc'
                                                ]
                                            }
                                            icon={IconName.RIGHT_ARROW}
                                            size="xsmall"
                                            klpColor="main"
                                            klpPalette="secondary"
                                            onClick={context.handleChangeSort(
                                                column,
                                            )}
                                        />
                                    )}
                                {column.sortable &&
                                    ((context.sortBy &&
                                        column.key !== context.sortBy.key) ||
                                        !context.sortBy) && (
                                        <Illustration
                                            className={classes.asc}
                                            icon={IconName.TRANSFER}
                                            size="xsmall"
                                            klpColor="main"
                                            klpPalette="primary"
                                            onClick={context.handleChangeSort(
                                                column,
                                            )}
                                        />
                                    )}
                            </div>
                        </MuiTableCell>
                    ),
                )}
            </MuiTableRow>
        </MuiTableHead>
    );
};

TableHead.displayName = 'TableHead';
